import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { getAllowedTeams } from '../../utils/teamPermissions';
import {
  Users,
  Calendar,
  Trophy,
  AlertTriangle,
  BarChart3,
  PlusCircle,
  TrendingUp,
  Clock,
  Target,
  ArrowLeft,
  ChevronRight,
  Zap,
  Activity
} from 'lucide-react';

const CoachDashboard = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [stats, setStats] = useState({
    totalPlayers: 0,
    upcomingTrainings: 0,
    upcomingMatches: 0,
    activeAlerts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);

      const allowedTeams = getAllowedTeams(userProfile);

      // Total de jugadores activos
      let playersQuery = supabase
        .from('players')
        .select('id', { count: 'exact' })
        .eq('active', true);

      if (allowedTeams) {
        playersQuery = playersQuery.in('team', allowedTeams);
      }

      const { count: playersCount } = await playersQuery;

      // Entrenamientos próximos (próximos 7 días)
      const today = new Date().toISOString().split('T')[0];
      const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      let trainingsQuery = supabase
        .from('trainings')
        .select('id', { count: 'exact' })
        .gte('date', today)
        .lte('date', nextWeek);

      if (allowedTeams) {
        trainingsQuery = trainingsQuery.in('team', allowedTeams);
      }

      const { count: trainingsCount } = await trainingsQuery;

      // Partidos próximos (próximos 7 días)
      let matchesQuery = supabase
        .from('matches')
        .select('id', { count: 'exact' })
        .gte('date', today)
        .lte('date', nextWeek);

      if (allowedTeams) {
        matchesQuery = matchesQuery.in('team', allowedTeams);
      }

      const { count: matchesCount } = await matchesQuery;

      // Alertas activas
      const { count: alertsCount } = await supabase
        .from('player_alerts')
        .select('id', { count: 'exact' })
        .eq('is_active', true);

      setStats({
        totalPlayers: playersCount || 0,
        upcomingTrainings: trainingsCount || 0,
        upcomingMatches: matchesCount || 0,
        activeAlerts: alertsCount || 0
      });

    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const modules = [
    {
      name: 'Jugadores',
      description: 'Gestionar fichas y estadísticas',
      icon: Users,
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      path: '/players',
      stat: `${stats.totalPlayers} jugadores`
    },
    {
      name: 'Entrenamientos',
      description: 'Planificar y registrar entrenamientos',
      icon: Calendar,
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      path: '/trainings',
      stat: `${stats.upcomingTrainings} próximos`
    },
    {
      name: 'Partidos',
      description: 'Gestionar partidos y convocatorias',
      icon: Trophy,
      color: 'bg-amber-500',
      hoverColor: 'hover:bg-amber-600',
      path: '/matches',
      stat: `${stats.upcomingMatches} próximos`
    },
    {
      name: 'Alertas',
      description: 'Seguimiento de jugadores',
      icon: AlertTriangle,
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600',
      path: '/alerts',
      stat: `${stats.activeAlerts} activas`
    },
    {
      name: 'Informes',
      description: 'Estadísticas y análisis',
      icon: BarChart3,
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      path: '/coach/reports',
      stat: 'Ver reportes'
    }
  ];

  const quickActions = [
    {
      name: 'Nuevo Entrenamiento',
      icon: PlusCircle,
      color: 'bg-purple-600',
      hoverColor: 'hover:bg-purple-700',
      path: '/trainings/new'
    },
    {
      name: 'Nuevo Partido',
      icon: PlusCircle,
      color: 'bg-amber-600',
      hoverColor: 'hover:bg-amber-700',
      path: '/matches/new'
    },
    {
      name: 'Registrar Jugador',
      icon: PlusCircle,
      color: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700',
      path: '/players/new'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-white/60">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Grid de fondo animado */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(220, 38, 38, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(220, 38, 38, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Efectos de luz ambiental */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-600/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="relative">
        {/* Header minimalista y elegante */}
        <div className="border-b border-white/5">
          <div className="max-w-7xl mx-auto px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-8">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="group relative w-12 h-12 flex items-center justify-center"
                >
                  <div className="absolute inset-0 bg-white/5 rounded-xl group-hover:bg-red-600/20 transition-all duration-300"></div>
                  <ArrowLeft className="relative w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
                </button>

                <div className="flex items-center gap-6">
                  <div className="relative">
                    <img
                      src="https://files.fcf.cat/escudos/clubes/escudos/00100_0001111944_pd.jpg"
                      alt="FC Puigdalber"
                      className="w-16 h-16 object-contain"
                    />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white mb-1">Panel del Entrenador</h1>
                    <p className="text-white/40 text-sm">
                      {userProfile?.full_name} · {userProfile?.team || 'Todos los equipos'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 text-white/40 text-sm">
                <Activity className="w-4 h-4" />
                <span>Sistema activo</span>
              </div>
            </div>
          </div>
        </div>

        {/* Estadísticas en layout horizontal innovador */}
        <div className="max-w-7xl mx-auto px-8 py-12">
          <div className="grid grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden">
            {/* Jugadores */}
            <div className="group bg-black p-8 relative overflow-hidden hover:bg-white/[0.02] transition-all cursor-pointer">
              <div className="absolute top-0 left-0 w-1 h-full bg-red-600 transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500"></div>
              <div className="relative">
                <Users className="w-8 h-8 text-red-600 mb-6" />
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-5xl font-bold text-white tabular-nums">{stats.totalPlayers}</span>
                </div>
                <p className="text-white/40 text-sm uppercase tracking-wider">Jugadores</p>
              </div>
            </div>

            {/* Entrenamientos */}
            <div className="group bg-black p-8 relative overflow-hidden hover:bg-white/[0.02] transition-all cursor-pointer">
              <div className="absolute top-0 left-0 w-1 h-full bg-red-600 transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500"></div>
              <div className="relative">
                <Calendar className="w-8 h-8 text-red-600 mb-6" />
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-5xl font-bold text-white tabular-nums">{stats.upcomingTrainings}</span>
                </div>
                <p className="text-white/40 text-sm uppercase tracking-wider">Entrenamientos</p>
              </div>
            </div>

            {/* Partidos */}
            <div className="group bg-black p-8 relative overflow-hidden hover:bg-white/[0.02] transition-all cursor-pointer">
              <div className="absolute top-0 left-0 w-1 h-full bg-red-600 transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500"></div>
              <div className="relative">
                <Trophy className="w-8 h-8 text-red-600 mb-6" />
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-5xl font-bold text-white tabular-nums">{stats.upcomingMatches}</span>
                </div>
                <p className="text-white/40 text-sm uppercase tracking-wider">Partidos</p>
              </div>
            </div>

            {/* Alertas */}
            <div className="group bg-red-950/20 p-8 relative overflow-hidden hover:bg-red-950/30 transition-all cursor-pointer">
              <div className="absolute top-0 left-0 w-1 h-full bg-red-600 scale-y-100"></div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-6">
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                  {stats.activeAlerts > 0 && (
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-5xl font-bold text-red-500 tabular-nums">{stats.activeAlerts}</span>
                </div>
                <p className="text-red-500/60 text-sm uppercase tracking-wider">Alertas Activas</p>
              </div>
            </div>
          </div>
        </div>

        {/* Acciones rápidas - Layout de lista horizontal */}
        <div className="max-w-7xl mx-auto px-8 pb-12">
          <div className="mb-6">
            <h2 className="text-white/40 text-xs uppercase tracking-widest">Acciones Rápidas</h2>
          </div>
          <div className="flex gap-4">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.name}
                  onClick={() => navigate(action.path)}
                  className="group flex-1 relative bg-white/[0.02] border border-white/5 rounded-xl p-6 hover:border-red-600/50 transition-all duration-300 overflow-hidden"
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-red-600/0 to-red-600/0 group-hover:from-red-600/10 group-hover:to-transparent transition-all duration-500"></div>
                  <div className="relative flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-red-600/20 transition-all">
                      <Icon className="w-6 h-6 text-white/60 group-hover:text-red-500 transition-colors" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-white font-medium mb-1">{action.name}</p>
                      <p className="text-white/30 text-xs">Crear ahora</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-red-600 group-hover:translate-x-1 transition-all" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Módulos - Grid asimétrico moderno */}
        <div className="max-w-7xl mx-auto px-8 pb-16">
          <div className="mb-6">
            <h2 className="text-white/40 text-xs uppercase tracking-widest">Módulos de Gestión</h2>
          </div>

          <div className="grid grid-cols-3 gap-6">
            {modules.map((module, index) => {
              const Icon = module.icon;
              const isAlerts = module.name === 'Alertas';
              const isReports = module.name === 'Informes';

              return (
                <button
                  key={module.name}
                  onClick={() => navigate(module.path)}
                  className={`group relative bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden hover:border-red-600/50 transition-all duration-500 ${
                    isReports ? 'col-span-3 row-span-1' : ''
                  }`}
                  style={{ transitionDelay: `${index * 75}ms` }}
                >
                  {/* Efecto de hover gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br transition-all duration-700 ${
                    isAlerts
                      ? 'from-red-950/20 to-transparent group-hover:from-red-950/40'
                      : 'from-red-600/0 to-red-600/0 group-hover:from-red-600/5 group-hover:to-transparent'
                  }`}></div>

                  <div className={`relative ${isReports ? 'flex items-center' : ''} p-8`}>
                    {/* Lado izquierdo - contenido */}
                    <div className={isReports ? 'flex-1' : ''}>
                      <div className="flex items-center justify-between mb-6">
                        <div className={`rounded-xl ${
                          isAlerts ? 'bg-red-950/40' : 'bg-white/5'
                        } p-3 group-hover:scale-110 transition-transform duration-300`}>
                          <Icon className={`w-7 h-7 ${
                            isAlerts ? 'text-red-500' : 'text-red-600'
                          }`} />
                        </div>
                        {isAlerts && stats.activeAlerts > 0 && (
                          <span className="relative flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
                          </span>
                        )}
                      </div>

                      <h3 className="text-2xl font-bold text-white mb-2">{module.name}</h3>
                      <p className="text-white/40 text-sm mb-6">{module.description}</p>

                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-medium ${
                          isAlerts ? 'text-red-500' : 'text-white/60'
                        }`}>
                          {module.stat}
                        </span>
                        <ChevronRight className={`w-4 h-4 ${
                          isAlerts ? 'text-red-600' : 'text-white/40'
                        } group-hover:text-red-600 group-hover:translate-x-1 transition-all`} />
                      </div>
                    </div>

                    {/* Lado derecho - visualización para informes */}
                    {isReports && (
                      <div className="w-64 h-32 relative">
                        <div className="absolute inset-0 flex items-end justify-around px-4">
                          {[40, 70, 50, 85, 65, 90].map((height, i) => (
                            <div
                              key={i}
                              className="w-8 bg-gradient-to-t from-red-600 to-red-500 rounded-t opacity-60 group-hover:opacity-100 transition-all"
                              style={{
                                height: `${height}%`,
                                transitionDelay: `${i * 50}ms`
                              }}
                            ></div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer con consejo - Diseño minimalista */}
        <div className="max-w-7xl mx-auto px-8 pb-16">
          <div className="relative bg-white/[0.02] border border-white/5 rounded-2xl p-8 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/5 rounded-full blur-3xl"></div>
            <div className="relative flex items-start gap-6">
              <div className="w-12 h-12 rounded-xl bg-red-950/40 flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-2">Consejo del Sistema</h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  Mantén un seguimiento constante de las <span className="text-red-500">alertas activas</span> para optimizar el rendimiento.
                  Usa el módulo de informes para analizar tendencias y tomar decisiones estratégicas basadas en datos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachDashboard;
